import { createLayer } from "@/api/hooks/layer";
import { replaceLayouts } from "@/api/hooks/layout";
import { createProfile, fetchSlimProfiles, setProfileTags } from "@/api/hooks/profile";
import { createTab, fetchTab } from "@/api/hooks/tab";
import { createLocationTable } from "@/api/hooks/tools/locationTable";
import { createMapWithViewport } from "@/api/hooks/tools/map";
import { createNote } from "@/api/hooks/tools/note";
import { createPlot } from "@/api/hooks/tools/plot";
import { createWeatherTable } from "@/api/hooks/tools/weatherTable";
import {
  createNewLocationTable,
  createNewNote,
  createNewPlot,
  createNewWeatherTable,
  newTab,
} from "@/constants/create";
import { SelfTransitionEventType, TransitionEventType } from "@/models/time-control/timeStateTypes";
import type { TimeStateMachine } from "@/models/time-control/timeStateTypes";
import type { AppDispatch } from "@/reducer";
import { saveTabSnapshotImmediate } from "@/reducer/auto-save/subscription";
import type { PlotCreate } from "@/reducer/client-models";
import { pushTabSnapshotForUndo } from "@/reducer/undo/pushSnapshot";
import type { AiChatAction, AiChatActionArgs, AiChatMessageMeta } from "@/types/ai-chat";
import type {
  CartographicMapCreate,
  GenericLayoutCreate,
  GridCellLayout,
  LayerIn,
  LocationTableCreate,
  NoteCreate,
  SlimProfile,
  TabSnapshot,
  WeatherTableCreate,
} from "@mm/metx-workbench.meteomatics.com";
import type { QueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import type { InterpreterFrom } from "xstate";

type ParsedAiChatAction = {
  name: string;
  args: Record<string, unknown>;
  raw: AiChatAction;
};

type LayoutKind = "map" | "plot" | "notes" | "weather_table" | "location_table";

const LAYOUT_KIND_MAP: LayoutKind = "map";
const LAYOUT_KIND_PLOT: LayoutKind = "plot";
const LAYOUT_KIND_NOTES: LayoutKind = "notes";
const LAYOUT_KIND_WEATHER_TABLE: LayoutKind = "weather_table";
const LAYOUT_KIND_LOCATION_TABLE: LayoutKind = "location_table";

// TODO: Do we even need it?
const ensurePlotLocationLng = (plotPayload: unknown) => {
  if (!plotPayload || typeof plotPayload !== "object") {
    return;
  }
  const location = (plotPayload as any).location;
  if (location && typeof location === "object" && !("lng" in location) && "lon" in location) {
    (location as Record<string, unknown>).lng = (location as Record<string, unknown>).lon;
  }
};

// TODO: Do we even need it?
const normalizeLayoutKind = (toolType: string | null | undefined): LayoutKind | undefined => {
  const normalized = toolType?.toLowerCase().trim();
  if (!normalized) {
    return undefined;
  }

  const canonical = normalized.replace(/[\s-]+/g, "_");

  if (canonical === LAYOUT_KIND_MAP) {
    return LAYOUT_KIND_MAP;
  }
  if (canonical === LAYOUT_KIND_PLOT) {
    return LAYOUT_KIND_PLOT;
  }
  if (canonical === LAYOUT_KIND_NOTES || canonical === "note") {
    return LAYOUT_KIND_NOTES;
  }
  if (canonical === LAYOUT_KIND_WEATHER_TABLE || canonical === "weathertable") {
    return LAYOUT_KIND_WEATHER_TABLE;
  }
  if (canonical === LAYOUT_KIND_LOCATION_TABLE || canonical === "locationtable") {
    return LAYOUT_KIND_LOCATION_TABLE;
  }
  return undefined;
};

// TODO: Looks super weird -> investigate usage
const findLayoutIdInSnapshot = (
  snapshot: TabSnapshot | null | undefined,
  kind: LayoutKind | undefined,
): number | undefined => {
  if (!kind) {
    return undefined;
  }
  if (!snapshot?.layouts || !Array.isArray(snapshot.layouts)) {
    return undefined;
  }
  const match = snapshot.layouts.find((layout) => {
    if (!layout || typeof layout !== "object") {
      return false;
    }
    const layoutType = normalizeLayoutKind(
      typeof (layout as any).type === "string" ? ((layout as any).type as string) : String((layout as any).type ?? ""),
    );
    if (layoutType !== kind) {
      return false;
    }
    return (layout as any).id_tool == null;
  });
  const layoutId = match ? (match as any).id : undefined;
  return typeof layoutId === "number" ? layoutId : undefined;
};

// TODO: What's this wrapper for?
const findLayoutIdFromSnapshot = (ctx: AiChatActionContext, kind: LayoutKind | undefined): number | undefined =>
  findLayoutIdInSnapshot(ctx.getCurrentTabSnapshot(), kind);

type ActionRuntimeState = {
  layoutQueueByTab: Map<number, Map<LayoutKind, number[]>>;
  tabRefreshesByTab: Map<number, Promise<TabSnapshot | null>>;
  layoutById: Map<number, unknown>;
};

const recordAvailableLayouts = (runtime: ActionRuntimeState, tabId: number, layouts: unknown[]) => {
  const queueForTab: Map<LayoutKind, number[]> = new Map();
  for (const layout of layouts) {
    if (!layout || typeof layout !== "object") {
      continue;
    }
    const layoutId = (layout as any).id;
    const layoutKind = normalizeLayoutKind(
      typeof (layout as any).type === "string" ? ((layout as any).type as string) : String((layout as any).type ?? ""),
    );
    const assignedTool = (layout as any).id_tool;
    if (typeof layoutId !== "number" || layoutKind === undefined) {
      continue;
    }
    runtime.layoutById.set(layoutId, layout);
    if (assignedTool != null) {
      continue;
    }
    const existing = queueForTab.get(layoutKind) ?? [];
    existing.push(layoutId);
    queueForTab.set(layoutKind, existing);
  }

  if (queueForTab.size > 0) {
    runtime.layoutQueueByTab.set(tabId, queueForTab);
  } else {
    runtime.layoutQueueByTab.delete(tabId);
  }
};

const popQueuedLayoutId = (
  runtime: ActionRuntimeState,
  tabId: number,
  layoutKind: LayoutKind | undefined,
): number | undefined => {
  if (!layoutKind) {
    return undefined;
  }
  const queue = runtime.layoutQueueByTab.get(tabId);
  if (!queue) {
    return undefined;
  }
  const ids = queue.get(layoutKind);
  if (!ids || ids.length === 0) {
    return undefined;
  }
  const [next, ...rest] = ids;
  if (rest.length > 0) {
    queue.set(layoutKind, rest);
  } else {
    queue.delete(layoutKind);
  }
  if (queue.size === 0) {
    runtime.layoutQueueByTab.delete(tabId);
  }
  return next;
};

const refreshTabSnapshot = async (
  ctx: AiChatActionContext,
  runtime: ActionRuntimeState,
  tabId: number,
): Promise<TabSnapshot | null> => {
  const fetchPromise = ctx
    .dispatch(fetchTab({ tabId }))
    .unwrap()
    .then(() => ctx.getCurrentTabSnapshot())
    .catch((err) => {
      console.error("aiChat refreshTabSnapshot failed", err);
      return null;
    });
  runtime.tabRefreshesByTab.set(tabId, fetchPromise);
  try {
    const snapshot = await fetchPromise;
    if (snapshot?.layouts) {
      recordAvailableLayouts(runtime, tabId, snapshot.layouts);
    }
    return snapshot;
  } finally {
    const current = runtime.tabRefreshesByTab.get(tabId);
    if (current === fetchPromise) {
      runtime.tabRefreshesByTab.delete(tabId);
    }
  }
};

const awaitPendingTabRefresh = async (runtime: ActionRuntimeState, tabId: number): Promise<TabSnapshot | null> => {
  const pending = runtime.tabRefreshesByTab.get(tabId);
  if (!pending) {
    return null;
  }
  try {
    return await pending;
  } catch {
    return null;
  }
};

const resolveLayoutIdForTool = async (
  ctx: AiChatActionContext,
  runtime: ActionRuntimeState,
  tabId: number,
  layoutKind: LayoutKind | undefined,
  explicitLayoutId?: number,
): Promise<number | undefined> => {
  if (!tabId) {
    return explicitLayoutId;
  }
  if (!layoutKind) {
    return explicitLayoutId;
  }
  if (explicitLayoutId) {
    return explicitLayoutId;
  }

  const fromQueue = popQueuedLayoutId(runtime, tabId, layoutKind);
  if (fromQueue) {
    return fromQueue;
  }

  const fromSnapshot = findLayoutIdFromSnapshot(ctx, layoutKind);
  if (fromSnapshot) {
    return fromSnapshot;
  }

  const pendingSnapshot = await awaitPendingTabRefresh(runtime, tabId);
  const fromPending =
    popQueuedLayoutId(runtime, tabId, layoutKind) ?? findLayoutIdInSnapshot(pendingSnapshot, layoutKind);
  if (fromPending) {
    return fromPending;
  }

  const refreshedSnapshot = await refreshTabSnapshot(ctx, runtime, tabId);
  return popQueuedLayoutId(runtime, tabId, layoutKind) ?? findLayoutIdInSnapshot(refreshedSnapshot, layoutKind);
};

// TODO: Investigate usage, do we need it?
const extractGridCellLayout = (candidate: unknown): GridCellLayout | undefined => {
  const value = (candidate as any)?.gridCellLayout ?? (candidate as any)?.gridcelllayout;
  if (!value || typeof value !== "object") {
    return undefined;
  }
  const gridColumnStart = (value as any).gridColumnStart;
  const gridColumnEnd = (value as any).gridColumnEnd;
  const gridRowStart = (value as any).gridRowStart;
  const gridRowEnd = (value as any).gridRowEnd;
  if (
    typeof gridColumnStart === "number" &&
    typeof gridColumnEnd === "number" &&
    typeof gridRowStart === "number" &&
    typeof gridRowEnd === "number"
  ) {
    return { gridColumnStart, gridColumnEnd, gridRowStart, gridRowEnd };
  }
  return undefined;
};

// TODO: Check if we don't have this default somewhere else
const defaultGridCellLayout = (): GridCellLayout => ({
  gridColumnStart: 1,
  gridColumnEnd: 101,
  gridRowStart: 1,
  gridRowEnd: 101,
});

const getLayoutById = (
  ctx: AiChatActionContext,
  runtime: ActionRuntimeState,
  layoutId: number,
): unknown | undefined => {
  if (runtime.layoutById.has(layoutId)) {
    return runtime.layoutById.get(layoutId);
  }
  const snapshot = ctx.getCurrentTabSnapshot();
  return snapshot?.layouts?.find((layout) => (layout as any)?.id === layoutId);
};

// TODO: Instead of crazy guardrails and fallback we should consume what comes from BE, but have it wrapper in try/catch and just fail gracefully
// Same case for other functions
// Also function is quite ridiculous, and huge, do we need to go through every property like that?
const buildPlotPayload = (
  payload: Record<string, unknown>,
  layout: unknown | undefined,
  fallbackGridCell?: GridCellLayout,
): PlotCreate => {
  const gridCellLayout = extractGridCellLayout(layout) ?? extractGridCellLayout(payload) ?? fallbackGridCell;
  const base = createNewPlot(gridCellLayout ?? defaultGridCellLayout());
  const merged: PlotCreate = { ...base, ...(payload as Partial<PlotCreate>) };

  const location =
    payload.location && typeof payload.location === "object"
      ? (payload.location as PlotCreate["location"])
      : base.location;
  ensurePlotLocationLng(location);
  const lat = typeof (location as any)?.lat === "number" ? (location as any).lat : undefined;
  const lon =
    typeof (location as any)?.lon === "number"
      ? (location as any).lon
      : typeof (location as any)?.lng === "number"
        ? (location as any).lng
        : undefined;

  const plotSeries =
    Array.isArray((payload as any)?.plotSeries) && (payload as any)?.plotSeries?.length
      ? ((payload as any).plotSeries as PlotCreate["plotSeries"])
      : base.plotSeries;
  const parameter = (payload as any)?.parameter;
  if (parameter && plotSeries?.[0] && typeof plotSeries[0] === "object") {
    (plotSeries[0] as any).parameter_unit = parameter;
  }

  merged.gridCellLayout = gridCellLayout ?? base.gridCellLayout;
  merged.plotSeries = plotSeries;
  merged.plotYAxis =
    Array.isArray((payload as any)?.plotYAxis) && (payload as any)?.plotYAxis?.length
      ? ((payload as any).plotYAxis as PlotCreate["plotYAxis"])
      : base.plotYAxis;
  merged.location = location;
  merged.location_info =
    (payload as any)?.location_info ??
    base.location_info ??
    (lat != null && lon != null ? { coordinates: { lat, lon } } : undefined);
  merged.title = typeof (payload as any)?.title === "string" ? ((payload as any).title as string) : base.title;
  merged.resolution = (payload as any)?.resolution ?? base.resolution ?? "PT1H";
  merged.datetime_from = (payload as any)?.datetime_from ?? base.datetime_from ?? null;
  merged.datetime_to = (payload as any)?.datetime_to ?? base.datetime_to ?? null;
  merged.datetime_from_offset = (payload as any)?.datetime_from_offset ?? base.datetime_from_offset ?? null;
  merged.datetime_to_offset = (payload as any)?.datetime_to_offset ?? base.datetime_to_offset ?? null;
  merged.time_lock = (payload as any)?.time_lock ?? base.time_lock ?? false;
  merged.display_time_plotline_visible =
    (payload as any)?.display_time_plotline_visible ?? base.display_time_plotline_visible ?? null;
  merged.now_time_plotline_visible =
    (payload as any)?.now_time_plotline_visible ?? base.now_time_plotline_visible ?? null;

  return merged;
};

const layoutSignature = (layout: any): string | null => {
  const grid = layout?.gridCellLayout ?? layout?.gridcelllayout;
  if (!grid) {
    return null;
  }
  const { gridColumnStart, gridColumnEnd, gridRowStart, gridRowEnd } = grid;
  if (
    typeof gridColumnStart !== "number" ||
    typeof gridColumnEnd !== "number" ||
    typeof gridRowStart !== "number" ||
    typeof gridRowEnd !== "number"
  ) {
    return null;
  }
  const type = typeof layout?.type === "string" ? layout.type : String(layout?.type ?? "");
  return `${type}|${gridColumnStart}-${gridColumnEnd}-${gridRowStart}-${gridRowEnd}`;
};

const preserveExistingToolsInLayouts = (
  layouts: GenericLayoutCreate[],
  tabId: number,
  ctx: AiChatActionContext,
): GenericLayoutCreate[] => {
  if (!Array.isArray(layouts) || layouts.length === 0) {
    return layouts;
  }
  const snapshot = ctx.getCurrentTabSnapshot();
  const snapshotLayouts = Array.isArray(snapshot?.layouts) ? snapshot.layouts : [];
  const lookup: Map<string, number> = new Map();
  const claimedTools: Set<number> = new Set();

  for (const layout of snapshotLayouts) {
    if (!layout || typeof layout !== "object") {
      continue;
    }
    if ((layout as any).id_tab && (layout as any).id_tab !== tabId) {
      continue;
    }
    const idTool = typeof (layout as any).id_tool === "number" ? ((layout as any).id_tool as number) : null;
    const sig = layoutSignature(layout);
    if (sig && idTool !== null && !lookup.has(sig)) {
      lookup.set(sig, idTool);
    }
  }

  const next: GenericLayoutCreate[] = [];
  for (const layout of layouts) {
    if (!layout || typeof layout !== "object") {
      continue;
    }
    if ((layout as any).id_tool != null) {
      const idTool = (layout as any).id_tool;
      if (typeof idTool === "number") {
        claimedTools.add(idTool);
      }
      next.push(layout);
      continue;
    }
    const sig = layoutSignature(layout);
    if (sig && lookup.has(sig)) {
      const candidate = lookup.get(sig);
      if (candidate != null && !claimedTools.has(candidate)) {
        claimedTools.add(candidate);
        next.push({ ...(layout as GenericLayoutCreate), id_tool: candidate });
        continue;
      }
    }
    next.push({ ...(layout as GenericLayoutCreate), id_tool: null });
  }

  return next;
};

const pickArg =
  <T>(keys: string[]) =>
  (args: Record<string, unknown>): T | undefined => {
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(args, key)) {
        return args[key] as T;
      }
    }
    return undefined;
  };

const getNumberArg = pickArg<number>(["tabId", "tab_id", "tabIdLegacy"]);
const getTabSnapshot = pickArg<TabSnapshot>(["tabSnapshot", "tab_snapshot"]);
const getChanges = pickArg<Record<string, unknown>>(["changes", "changeSet"]);
const getLayouts = pickArg<GenericLayoutCreate[]>(["layouts"]);
const getToolType = pickArg<string>(["toolType", "tool_type"]);
const getMapId = pickArg<number>(["mapId", "map_id"]);
const getProfileId = pickArg<number>(["profileId", "profile_id"]);
const getLayoutId = pickArg<number>(["layoutId", "layout_id"]);
const getPayload = pickArg<Record<string, unknown>>(["payload"]);

const parsePathSegments = (path: string): Array<string | number> =>
  (path.match(/[^.[\]]+/g) || []).map((segment) => {
    const numeric = Number(segment);
    if (!Number.isNaN(numeric) && String(numeric) === segment) {
      return numeric;
    }
    return segment;
  });

const applyChangeSet = (snapshot: TabSnapshot, changes: Record<string, unknown>): TabSnapshot => {
  const clone =
    typeof (globalThis as any).structuredClone === "function"
      ? (globalThis as any).structuredClone(snapshot)
      : (JSON.parse(JSON.stringify(snapshot)) as TabSnapshot);

  const setByPath = (segments: Array<string | number>, value: unknown) => {
    let cursor: any = clone;
    for (let i = 0; i < segments.length; i += 1) {
      const segment = segments[i];
      const isLast = i === segments.length - 1;
      const nextIsIndex = typeof segments[i + 1] === "number";

      if (isLast) {
        if (typeof segment === "number" && Array.isArray(cursor)) {
          cursor[segment] = value;
        } else if (cursor && typeof cursor === "object") {
          cursor[segment] = value;
        }
        return;
      }

      if (typeof segment === "number") {
        if (!Array.isArray(cursor)) {
          return;
        }
        if (cursor[segment] === undefined || cursor[segment] === null || typeof cursor[segment] !== "object") {
          cursor[segment] = nextIsIndex ? [] : {};
        }
        cursor = cursor[segment];
      } else {
        if (!cursor || typeof cursor !== "object") {
          return;
        }
        if (cursor[segment] === undefined || cursor[segment] === null || typeof cursor[segment] !== "object") {
          cursor[segment] = nextIsIndex ? [] : {};
        }
        cursor = cursor[segment];
      }
    }
  };

  for (const [path, value] of Object.entries(changes || {})) {
    const segments = parsePathSegments(path);
    if (segments.length === 0) {
      continue;
    }
    setByPath(segments, value);
  }

  return clone;
};

const defaultViewport = () => ({
  kind: "ViewportCenterPoint",
  center_lng: 0,
  center_lat: 0,
  zoom: 0,
  southWest_lng: 0,
  southWest_lat: 0,
  northEast_lng: 0,
  northEast_lat: 0,
});

const normalizeDashboardPayload = (profilePayload: any) => {
  const normalized = { ...(profilePayload || {}) };
  if (Array.isArray(normalized.tabs)) {
    normalized.tabs = normalized.tabs.map((tab: any) => {
      const nextTab = { ...(tab || {}) };
      const maps: any[] = Array.isArray(nextTab.maps) ? nextTab.maps : [];
      let viewports: any[] = Array.isArray(nextTab.viewports) ? nextTab.viewports : [];
      if (maps.length > 0) {
        if (viewports.length < maps.length) {
          const missing = maps.length - viewports.length;
          viewports = [...viewports, ...Array.from({ length: missing }, () => defaultViewport())];
        } else if (viewports.length > maps.length) {
          viewports = viewports.slice(0, maps.length);
        }
      }
      nextTab.viewports = viewports;
      return nextTab;
    });
  }
  return normalized;
};

const normalizeTabPayload = (tabPayload: any) => {
  if (!tabPayload) {
    return null;
  }
  const nextTab = { ...(tabPayload || {}) };
  const maps: any[] = Array.isArray(nextTab.maps) ? nextTab.maps : [];
  let viewports: any[] = Array.isArray(nextTab.viewports) ? nextTab.viewports : [];
  if (maps.length > 0) {
    if (viewports.length < maps.length) {
      const missing = maps.length - viewports.length;
      viewports = [...viewports, ...Array.from({ length: missing }, () => defaultViewport())];
    } else if (viewports.length > maps.length) {
      viewports = viewports.slice(0, maps.length);
    }
  }
  nextTab.viewports = viewports;
  return nextTab;
};

const hasInvalidLayerPayload = (value: unknown): boolean => {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.some((layer) => {
    if (!layer || typeof layer !== "object") {
      return false;
    }
    const id = (layer as any).id;
    const id_profile = (layer as any).id_profile;
    const id_cartographicmap = (layer as any).id_cartographicmap;
    return id == null || id_profile == null || id_cartographicmap == null;
  });
};

const buildEmptyDashboardPayload = (title: string) => ({
  time_state: null,
  title,
  tabs: [newTab()],
  use_global_datetime: false,
});

export interface AiChatActionContext {
  dispatch: AppDispatch;
  queryClient: QueryClient;
  timeService?: InterpreterFrom<TimeStateMachine>;
  activeProfileId: number | null;
  assistantMessageId?: string;
  getCurrentTabSnapshot: () => TabSnapshot | null;
  markMessageMeta?: (assistantId: string, messageMeta: AiChatMessageMeta) => void;
}

type AiChatActionHandler = (action: ParsedAiChatAction, ctx: AiChatActionContext) => Promise<void>;

const createToolForLayout = async (
  ctx: AiChatActionContext,
  tabId: number,
  layoutId: number,
  layoutKind: LayoutKind,
  payload: unknown,
): Promise<void> => {
  if (layoutKind === LAYOUT_KIND_MAP) {
    await ctx
      .dispatch(
        createMapWithViewport({
          tabId,
          data: payload as CartographicMapCreate,
          layoutId,
        }),
      )
      .unwrap();
    return;
  }

  if (layoutKind === LAYOUT_KIND_PLOT) {
    ensurePlotLocationLng(payload);
    await ctx
      .dispatch(
        createPlot({
          tabId,
          data: payload as PlotCreate,
          layoutId,
        }),
      )
      .unwrap();
    return;
  }

  if (layoutKind === LAYOUT_KIND_NOTES) {
    const base = createNewNote(tabId);
    const payloadObj = payload && typeof payload === "object" && !Array.isArray(payload) ? (payload as object) : {};
    const data = { ...base, ...(payloadObj as Partial<NoteCreate>), id_tab: tabId };
    await ctx.dispatch(createNote({ data, layoutId })).unwrap();
    return;
  }

  if (layoutKind === LAYOUT_KIND_WEATHER_TABLE) {
    const base = createNewWeatherTable(tabId);
    const payloadObj = payload && typeof payload === "object" && !Array.isArray(payload) ? (payload as object) : {};
    const data = { ...base, ...(payloadObj as Partial<WeatherTableCreate>), id_tab: tabId };
    await ctx.dispatch(createWeatherTable({ data, layoutId })).unwrap();
    return;
  }

  if (layoutKind === LAYOUT_KIND_LOCATION_TABLE) {
    const base = createNewLocationTable(tabId);
    const payloadObj = payload && typeof payload === "object" && !Array.isArray(payload) ? (payload as object) : {};
    const data = { ...base, ...(payloadObj as Partial<LocationTableCreate>), id_tab: tabId };
    await ctx.dispatch(createLocationTable({ data, layoutId })).unwrap();
    return;
  }

  console.warn("createTool action unsupported layoutKind", layoutKind);
};

const createHandlers = (runtime: ActionRuntimeState): Record<string, AiChatActionHandler> => ({
  syncTabSnapshot: async (action, ctx) => {
    const tabId = getNumberArg(action.args);
    const changeSet = getChanges(action.args);
    const providedSnapshot = getTabSnapshot(action.args);

    if (changeSet && Object.keys(changeSet).length > 0) {
      if (Object.values(changeSet).some((value) => hasInvalidLayerPayload(value))) {
        console.warn("syncTabSnapshot skipped: changeSet contains layers without ids");
        return;
      }
    }
    if (
      providedSnapshot &&
      Array.isArray(providedSnapshot.maps) &&
      providedSnapshot.maps.some((m) => hasInvalidLayerPayload((m as any).layers))
    ) {
      console.warn("syncTabSnapshot skipped: provided snapshot contains layers without ids");
      return;
    }

    let snapshotToSave: TabSnapshot | null = null;

    if (changeSet && Object.keys(changeSet).length > 0) {
      const currentTabState = ctx.getCurrentTabSnapshot();
      if (!currentTabState) {
        console.warn("syncTabSnapshot action skipped: unable to build current snapshot");
        return;
      }
      snapshotToSave = applyChangeSet(currentTabState, changeSet);
    } else if (providedSnapshot) {
      snapshotToSave = providedSnapshot;
    }

    const finalTabId = tabId ?? snapshotToSave?.tab_meta?.id ?? (providedSnapshot?.tab_meta as any)?.id;
    if (!finalTabId || !snapshotToSave) {
      console.warn("syncTabSnapshot action missing tabId or snapshot");
      return;
    }

    try {
      console.log("[aiChat] syncTabSnapshot apply", {
        tabId: finalTabId,
        hasChangeSet: !!changeSet,
        snapshotKeys: snapshotToSave ? Object.keys(snapshotToSave) : [],
      });
      ctx.dispatch(pushTabSnapshotForUndo());
      await saveTabSnapshotImmediate(finalTabId, snapshotToSave);
      ctx.dispatch(fetchTab({ tabId: finalTabId, skipUndoClear: true } as any));
    } catch (err) {
      console.error("syncTabSnapshot action failed", err);
    }
  },
  createDashboard: async (action, ctx) => {
    const tags = Array.isArray(action.args.tags)
      ? (action.args.tags.filter((t) => typeof t === "string") as string[])
      : [];
    const maybeProfilePayload =
      (action.args.profile as AiChatActionArgs["profile"]) ??
      (action.args.dashboard as AiChatActionArgs["dashboard"]) ??
      (action.args.payload as AiChatActionArgs["payload"]);

    const titleFromArgs =
      (typeof action.args.title === "string" ? (action.args.title as string) : undefined) ??
      (typeof action.args.dashboardTitle === "string" ? (action.args.dashboardTitle as string) : undefined) ??
      (typeof action.args.name === "string" ? (action.args.name as string) : undefined);

    const title =
      titleFromArgs ??
      (maybeProfilePayload && typeof (maybeProfilePayload as any).title === "string"
        ? ((maybeProfilePayload as any).title as string)
        : undefined);

    let profilePayload: unknown;
    if (maybeProfilePayload && typeof maybeProfilePayload === "object") {
      profilePayload = maybeProfilePayload;
    } else if (title) {
      profilePayload = buildEmptyDashboardPayload(title);
    } else {
      console.warn("createDashboard action missing profile payload or title");
      return;
    }

    try {
      const profile = await ctx.dispatch(createProfile(normalizeDashboardPayload(profilePayload) as any)).unwrap();
      if (tags.length > 0) {
        await ctx.dispatch(setProfileTags({ profileId: profile.id, tags })).unwrap();
      }
      const targetTabId = profile.tab_active || profile.tabs?.[0]?.id;
      if (targetTabId) {
        ctx.dispatch(fetchTab({ tabId: targetTabId }));
      }
      ctx.dispatch(fetchSlimProfiles());
      if (ctx.assistantMessageId) {
        const maybeProfile =
          profile && typeof profile === "object" && Array.isArray((profile as any).tabs)
            ? (profile as SlimProfile)
            : undefined;
        ctx.markMessageMeta?.(ctx.assistantMessageId, {
          createdDashboard: {
            profileId: profile.id,
            tabId: targetTabId ?? undefined,
          },
          createdDashboardProfile: maybeProfile,
        });
      }
    } catch (err) {
      console.error("createDashboard action failed", err);
    }
  },
  createTab: async (action, ctx) => {
    const profileId = getProfileId(action.args);
    const tabPayload =
      (action.args.tab as AiChatActionArgs["tab"]) ??
      (action.args.payload as AiChatActionArgs["payload"]) ??
      (action.args.tabSnapshot as AiChatActionArgs["tabSnapshot"]);

    if (!profileId || !tabPayload) {
      console.warn("createTab action missing profileId or tab payload");
      return;
    }

    const normalizedTab = normalizeTabPayload(tabPayload);
    if (!normalizedTab) {
      console.warn("createTab action payload could not be normalized");
      return;
    }

    try {
      const tab = await ctx.dispatch(createTab({ profileId, data: normalizedTab as any })).unwrap();
      if (tab?.id) {
        ctx.dispatch(fetchTab({ tabId: tab.id }));
      }
      ctx.dispatch(fetchSlimProfiles());
    } catch (err) {
      console.error("createTab action failed", err);
    }
  },
  createLayout: async (action, ctx) => {
    const tabId = getNumberArg(action.args);
    const layouts = getLayouts(action.args);
    if (!tabId || !Array.isArray(layouts) || layouts.length === 0) {
      console.warn("createLayout action missing tabId or layouts");
      return;
    }
    try {
      const stabilizedLayouts = preserveExistingToolsInLayouts(layouts, tabId, ctx);
      const newLayouts = await ctx.dispatch(replaceLayouts({ tabId, data: stabilizedLayouts })).unwrap();
      if (Array.isArray(newLayouts)) {
        recordAvailableLayouts(runtime, tabId, newLayouts as unknown[]);
      } else {
        runtime.layoutQueueByTab.delete(tabId);
      }
      const snapshot = await refreshTabSnapshot(ctx, runtime, tabId);
      if (snapshot?.tab_meta?.id) {
        await saveTabSnapshotImmediate(snapshot.tab_meta.id, snapshot);
      }
    } catch (err) {
      console.error("createLayout action failed", err);
    }
  },
  createTool: async (action, ctx) => {
    const tabId = getNumberArg(action.args);
    const toolType = getToolType(action.args);
    const payload = getPayload(action.args) ?? {};
    const explicitLayoutId = getLayoutId(action.args);
    const layoutKind = normalizeLayoutKind(toolType);

    if (!tabId || !toolType || !layoutKind) {
      console.warn("createTool action missing tabId or unsupported toolType", { tabId, toolType });
      return;
    }

    let layoutId = await resolveLayoutIdForTool(ctx, runtime, tabId, layoutKind, explicitLayoutId);

    if (!layoutId) {
      console.warn("createTool action could not resolve layoutId", { tabId, toolType, layoutKind, explicitLayoutId });
      return;
    }

    try {
      const layout =
        getLayoutById(ctx, runtime, layoutId) ??
        (await refreshTabSnapshot(ctx, runtime, tabId))?.layouts?.find((l) => (l as any)?.id === layoutId);
      if (!layout) {
        const resolved = await resolveLayoutIdForTool(ctx, runtime, tabId, layoutKind, explicitLayoutId);
        if (!resolved) {
          console.warn("createTool action could not resolve layout after refresh", { tabId, toolType, layoutKind });
          return;
        }
        const refreshedLayout =
          getLayoutById(ctx, runtime, resolved) ??
          (await refreshTabSnapshot(ctx, runtime, tabId))?.layouts?.find((l) => (l as any)?.id === resolved);
        if (!refreshedLayout) {
          console.warn("createTool action missing layout after re-resolution", { tabId, toolType, layoutId: resolved });
          return;
        }
        layoutId = resolved;
      }
      const finalPayload =
        layoutKind === LAYOUT_KIND_PLOT ? buildPlotPayload(payload, layout, extractGridCellLayout(layout)) : payload;
      await createToolForLayout(ctx, tabId, layoutId, layoutKind, finalPayload);

      const snapshot = ctx.getCurrentTabSnapshot();
      if (snapshot?.tab_meta?.id) {
        await saveTabSnapshotImmediate(snapshot.tab_meta.id, snapshot);
      }

      const refreshedSnapshot = await refreshTabSnapshot(ctx, runtime, tabId);
      if (refreshedSnapshot?.tab_meta?.id) {
        await saveTabSnapshotImmediate(refreshedSnapshot.tab_meta.id, refreshedSnapshot);
      }
    } catch (err) {
      console.error("createTool action failed", err);
    }
  },
  createLayer: async (action, ctx) => {
    const mapId = getMapId(action.args);
    const profileId = getProfileId(action.args) ?? ctx.activeProfileId;
    const layerPayload = (action.args.layer as Record<string, unknown> | undefined) ?? getPayload(action.args);
    if (!mapId || !profileId || !layerPayload) {
      console.warn("createLayer action missing mapId, profileId, or payload");
      return;
    }
    try {
      await ctx
        .dispatch(
          createLayer({
            mapId,
            profileId,
            data: layerPayload as unknown as LayerIn,
          }),
        )
        .unwrap();
      const associatedTabId = getNumberArg(action.args);
      if (associatedTabId) {
        ctx.dispatch(fetchTab({ tabId: associatedTabId }));
      }
    } catch (err) {
      console.error("createLayer action failed", err);
    }
  },
  updateTimeState: async (action, ctx) => {
    const profileId = getProfileId(action.args);
    if (!profileId) {
      console.warn("updateTimeState action missing profileId");
      return;
    }

    const snap = ctx.timeService?.getSnapshot?.();
    const canUseLocalService = ctx.timeService && snap && ctx.activeProfileId === profileId;

    if (canUseLocalService) {
      const currentStateVal = snap?.value as string;
      const desiredMode = (action.args.mode as string) || currentStateVal;
      if (desiredMode === "AUTO_REFRESH" && currentStateVal !== "AUTO_REFRESH") {
        ctx.timeService?.send({ type: TransitionEventType.setAutoRefreshMode });
      } else if (desiredMode === "ANIMATION" && currentStateVal !== "ANIMATION") {
        ctx.timeService?.send({ type: TransitionEventType.setAnimationMode });
      }

      if (action.args.displayTime) {
        const dt = DateTime.fromISO(String(action.args.displayTime), { setZone: true });
        if (dt.isValid) {
          ctx.timeService?.send({
            type: SelfTransitionEventType.updateDisplayTime,
            displayTime: dt,
          });
          ctx.timeService?.send({
            type: SelfTransitionEventType.updateNextDisplayTime,
            nextDisplayTime_: dt,
          });
        }
      }
      if (typeof action.args.refreshRateSeconds === "number") {
        ctx.timeService?.send({
          type: SelfTransitionEventType.updateRefreshRate,
          refreshRate: Math.max(0, action.args.refreshRateSeconds),
        });
      }
      if (typeof action.args.isLocked === "boolean") {
        const currentLocked = (snap?.context as any)?.modeOpts?.isLocked;
        if (currentLocked !== action.args.isLocked) {
          ctx.timeService?.send({ type: SelfTransitionEventType.toggleLockMode });
        }
      }
      if (action.args.timezone) {
        const tz =
          typeof action.args.timezone === "string" ? (action.args.timezone as string).toLowerCase() : undefined;
        if (tz === "utc" || tz === "local") {
          ctx.timeService?.send({
            type: SelfTransitionEventType.updateGuiTimezone,
            timezone: tz as any,
          });
        }
      }
      return;
    }

    console.warn("updateTimeState action skipped (no local time state service for profile)", { profileId });
    return;
  },
});

const parseAction = (action: AiChatAction | null | undefined): ParsedAiChatAction | null => {
  if (!action || typeof action.name !== "string") {
    return null;
  }
  if (action.args && typeof action.args === "object") {
    return { name: action.name, args: action.args as Record<string, unknown>, raw: action };
  }
  return { name: action.name, args: {}, raw: action };
};

export function createAiChatActionExecutor(ctx: AiChatActionContext) {
  const runtime: ActionRuntimeState = {
    layoutQueueByTab: new Map(),
    tabRefreshesByTab: new Map(),
    layoutById: new Map(),
  };
  const handlers = createHandlers(runtime);
  return async (actions: AiChatAction[] | null | undefined, assistantMessageId?: string) => {
    if (!actions || actions.length === 0) {
      return;
    }
    const ctxWithMessageId: AiChatActionContext = assistantMessageId ? { ...ctx, assistantMessageId } : ctx;
    for (const action of actions) {
      const parsed = parseAction(action);
      if (!parsed) {
        continue;
      }
      const handler = handlers[parsed.name];
      if (!handler) {
        console.warn("Unknown action", parsed.name);
        continue;
      }
      await handler(parsed, ctxWithMessageId);
    }
  };
}
